import {
	Box,
	Grid,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
} from "@chakra-ui/react";
import { createBlock } from "@wordpress/blocks";
import { dispatch, select, useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import {
	AlignBottom,
	AlignCenter,
	AlignLeft,
	AlignMiddle,
	AlignRight,
	AlignSpaceAround,
	AlignTop,
	ArrowDownAlt,
	ArrowRightAlt,
	BottomSeparator_Curve1,
	BottomSeparator_Curve2,
	BottomSeparator_Curve3,
	BottomSeparator_Rounded1,
	BottomSeparator_Rounded2,
	BottomSeparator_Rounded3,
	BottomSeparator_Slant1,
	BottomSeparator_Slant2,
	BottomSeparator_Straight,
	BottomSeparator_Wave1,
	BottomSeparator_Wave2,
	BottomSeparator_Wave3,
	BottomSeparator_Wave4,
	Seperator_None,
	TopSeparator_Curve1,
	TopSeparator_Curve2,
	TopSeparator_Curve3,
	TopSeparator_Rounded1,
	TopSeparator_Rounded2,
	TopSeparator_Rounded3,
	TopSeparator_Slant1,
	TopSeparator_Slant2,
	TopSeparator_Straight,
	TopSeparator_Wave1,
	TopSeparator_Wave2,
	TopSeparator_Wave3,
	TopSeparator_Wave4,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	BoxShadow,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	clientId?: string;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			verticalAlignment,
			container,
			width,
			columnGap,
			height,
			minHeight,
			background,
			hoverBackground,
			border,
			borderHover,
			boxShadow,
			boxShadowHover,
			overlay,
			overlayBackground,
			blockMargin,
			blockPadding,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			topSeparator,
			bottomSeparator,
			orientation,
			justification,
			alignment,
			rowGap,
		},
		setAttributes,
		clientId,
	} = props;

	// Get column information for inspector controls
	const { columnsIds } = useSelect(
		(select) => {
			const { getBlockOrder } = select("core/block-editor") as any;
			return {
				columnsIds: getBlockOrder(clientId),
			};
		},
		[clientId]
	);

	const addRemoveBlock = (type: string) => {
		const { getBlocks } = select("core/block-editor") as any;
		const { replaceInnerBlocks } = dispatch("core/block-editor");
		const innerBlocks = [...getBlocks(clientId)];

		if (type === "delete") {
			// Remove the last column
			innerBlocks.pop();
		} else {
			// Add a new column
			innerBlocks.push(createBlock("magazine-blocks/column"));
		}

		if (clientId) {
			replaceInnerBlocks(clientId, innerBlocks, false);
		}
	};

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")}>
					<Box>
						<Box fontSize="sm" color="gray.600" mb="8px">
							{__("Number of Columns", "magazine-blocks")}
						</Box>
						<NumberInput
							value={columnsIds.length}
							onChange={(valueString: string) => {
								const val = parseInt(valueString);
								if (isNaN(val)) return;

								const currentCount = columnsIds.length;
								const newCount = Math.max(1, Math.min(12, val));

								if (newCount > currentCount) {
									for (
										let i = currentCount;
										i < newCount;
										i++
									) {
										addRemoveBlock("add");
									}
								} else if (newCount < currentCount) {
									for (
										let i = currentCount;
										i > newCount;
										i--
									) {
										addRemoveBlock("delete");
									}
								}
							}}
							min={1}
							max={12}
							step={1}
							size="sm"
							sx={{
								".chakra-numberinput__field": {
									border: "1px solid #E2E8F0",
									borderRadius: "6px",
									backgroundColor: "white",
									color: "#718096",
									fontSize: "14px",
									padding: "8px 12px",
									"&:focus": {
										borderColor: "none",
										boxShadow: "none",
									},
									"&:hover": {
										borderColor: "none",
										boxShadow: "none",
									},
								},
								".chakra-numberinput__stepper": {
									border: "none",
									backgroundColor: "transparent",
								},
								".chakra-numberinput__stepper button": {
									border: "none",
									backgroundColor: "transparent",
									color: "#3182CE",
									fontSize: "16px",
									fontWeight: "bold",
									width: "32px",
									height: "32px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									cursor: "pointer",
									"&:hover": {
										backgroundColor: "#EBF8FF",
									},
									"&:active": {
										backgroundColor: "#BEE3F8",
									},
								},
							}}
						>
							<NumberInputField />
							<NumberInputStepper
								width="60%"
								display="flex"
								flexDirection="row"
							>
								<NumberIncrementStepper>
									<Box as="span">+</Box>
								</NumberIncrementStepper>
								<NumberDecrementStepper borderTopWidth="0 !important">
									<Box as="span">-</Box>
								</NumberDecrementStepper>
							</NumberInputStepper>
						</NumberInput>
					</Box>
					<ToggleButtonGroup
						value={container}
						onChange={(v) => {
							setAttributes({ container: v });
						}}
						label={__("Container", "magazine-blocks")}
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									color: "#1A202C",
									svg: {
										w: 6,
										h: 6,
									},
								},
								"&[data-active]": {
									color: "#fff",
								},
							},
						}}
					>
						<ToggleButton value="contained">
							{__("Contained", "magazine-blocks")}
						</ToggleButton>
						<ToggleButton value="stretched">
							{__("Stretched", "magazine-blocks")}
						</ToggleButton>
					</ToggleButtonGroup>
					<ToggleButtonGroup
						value={orientation}
						onChange={(v) => {
							setAttributes({ orientation: v });
						}}
						label={__("Orientation", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton
							leftIcon={<ArrowRightAlt />}
							value="horizontal"
						></ToggleButton>
						<ToggleButton
							leftIcon={<ArrowDownAlt />}
							value="vertical"
						></ToggleButton>
					</ToggleButtonGroup>
					<ToggleButtonGroup
						value={justification}
						onChange={(v) => {
							setAttributes({ justification: v });
						}}
						label={__("Justification", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton
							value={
								"vertical" === orientation?.desktop
									? "left"
									: "right"
							}
						>
							{"vertical" === orientation?.desktop ? (
								<AlignBottom />
							) : (
								<AlignLeft />
							)}
						</ToggleButton>
						<ToggleButton value="center">
							{"vertical" === orientation?.desktop ? (
								<AlignCenter />
							) : (
								<AlignCenter />
							)}
						</ToggleButton>
						<ToggleButton
							value={
								"vertical" === orientation?.desktop
									? "right"
									: "left"
							}
						>
							{"vertical" === orientation?.desktop ? (
								<AlignTop />
							) : (
								<AlignRight />
							)}
						</ToggleButton>
					</ToggleButtonGroup>
					<Box mt="18px">
						<ToggleButtonGroup
							value={alignment}
							onChange={(v) => {
								setAttributes({ alignment: v });
							}}
							label={__("Alignment", "blockart")}
							responsive
							groupProps={{
								sx: {
									".chakra-button": {
										minH: "32px",
										svg: {
											w: 6,
											h: 6,
										},
										"&[data-active]": {
											svg: {
												fill: "#fff !important",
											},
										},
									},
								},
							}}
						>
							<ToggleButton value="top">
								{"vertical" === orientation?.desktop ? (
									<AlignLeft />
								) : (
									<AlignTop />
								)}
							</ToggleButton>
							<ToggleButton value="center">
								{"vertical" === orientation?.desktop ? (
									<AlignMiddle />
								) : (
									<AlignCenter />
								)}
							</ToggleButton>
							<ToggleButton value="bottom">
								{"vertical" === orientation?.desktop ? (
									<AlignRight />
								) : (
									<AlignBottom />
								)}
							</ToggleButton>
							<ToggleButton value="stretch">
								<AlignSpaceAround />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					{"contained" === container && (
						<Slider
							onChange={(val: any) =>
								setAttributes({ width: val })
							}
							label={__("Max Width", "magazine-blocks")}
							responsive={true}
							min={0}
							max={1920}
							value={width}
							units={["px", "em"]}
							resetAttributeKey="width"
						/>
					)}
					{columnsIds.length > 1 && (
						<>
							<Slider
								onChange={(val: any) =>
									setAttributes({ columnGap: val })
								}
								label={__("Column Gap", "magazine-blocks")}
								units={["px", "em", "%"]}
								responsive={true}
								min={0}
								max={100}
								value={columnGap}
								resetAttributeKey="columnGap"
							/>
							<Slider
								label={__("Row Gap", "blockart")}
								value={rowGap}
								onChange={(value: any) =>
									setAttributes({ rowGap: value })
								}
								min={0}
								max={100}
								step={1}
								units={["px", "em", "rem", "%"]}
								responsive
								resetAttributeKey="rowGap"
							/>
						</>
					)}
					<Select
						options={[
							{
								label: __("Min Height", "magazine-blocks"),
								value: "min-height",
							},
							{
								label: __("Default", "magazine-blocks"),
								value: "default",
							},
							{
								label: __("Fit To Screen", "magazine-blocks"),
								value: "fit-to-screen",
							},
						]}
						onChange={(val) => setAttributes({ height: val })}
						value={height}
						label={__("Height", "magazine-blocks")}
					/>
					{"min-height" === height && (
						<Slider
							onChange={(val: any) =>
								setAttributes({ minHeight: val })
							}
							label={__("Min Height", "magazine-blocks")}
							units={["px", "em", "vh"]}
							responsive={true}
							min={0}
							max={1200}
							value={minHeight}
							resetAttributeKey="minHeight"
						/>
					)}
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Background", "magazine-blocks")}>
					<PopoverDrawer label={__("Type", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={background}
										resetKey="background"
										onReset={(v) => {
											setAttributes({ background: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={background}
										onChange={(v) =>
											setAttributes({ background: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={hoverBackground}
										resetKey="hoverBackground"
										onReset={(v) => {
											setAttributes({
												hoverBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={hoverBackground}
										onChange={(v) =>
											setAttributes({
												hoverBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Separator", "magazine-blocks")}>
					<Tabs position="relative">
						<TabList>
							<Tab>{__("Top", "magazine-blocks")}</Tab>
							<Tab>{__("Bottom", "magazine-blocks")}</Tab>
						</TabList>

						<TabPanels>
							{/* Top Separator */}
							<TabPanel p={2}>
								<Grid
									templateColumns={{
										base: "repeat(2, 1fr)",
										md: "repeat(3, 1fr)",
									}}
									gap={3}
								>
									{[
										{
											value: "none",
											icon: Seperator_None,
										},
										{
											value: "top_separator_1",
											icon: TopSeparator_Curve1,
										},
										{
											value: "top_separator_2",
											icon: TopSeparator_Curve2,
										},
										{
											value: "top_separator_3",
											icon: TopSeparator_Curve3,
										},
										{
											value: "top_separator_4",
											icon: TopSeparator_Rounded1,
										},
										{
											value: "top_separator_5",
											icon: TopSeparator_Rounded2,
										},
										{
											value: "top_separator_6",
											icon: TopSeparator_Rounded3,
										},
										{
											value: "top_separator_7",
											icon: TopSeparator_Slant1,
										},
										{
											value: "top_separator_8",
											icon: TopSeparator_Slant2,
										},
										{
											value: "top_separator_9",
											icon: TopSeparator_Straight,
										},
										{
											value: "top_separator_10",
											icon: TopSeparator_Wave1,
										},
										{
											value: "top_separator_11",
											icon: TopSeparator_Wave2,
										},
										{
											value: "top_separator_12",
											icon: TopSeparator_Wave3,
										},
										{
											value: "top_separator_13",
											icon: TopSeparator_Wave4,
										},
									].map((item, idx) => {
										const isSelected =
											topSeparator?.topSeparatorIcon ===
											item.value;

										return (
											<Box
												key={item.value}
												bg="gray.50"
												role="button"
												tabIndex={0}
												onClick={() =>
													setAttributes({
														topSeparator: {
															topSeparator: 1,
															topSeparatorIcon:
																item.value,
														},
													})
												}
												borderRadius="base"
												border={
													isSelected
														? "2px solid"
														: "1px solid"
												}
												borderColor={
													isSelected
														? "purple.500"
														: "gray.200"
												}
												height="70px"
												display="flex"
												flexDirection="column"
												alignItems="center"
												justifyContent="center"
												cursor="pointer"
												p={1}
											>
												<Box
													as={item.icon}
													width="full"
													height="full"
												/>
												<Text
													fontSize="xs"
													fontWeight="semibold"
													mb={0}
													pt={0.5}
												>
													{__(
														"Style",
														"magazine-blocks"
													)}{" "}
													{idx + 1}
												</Text>
											</Box>
										);
									})}
								</Grid>
							</TabPanel>

							<TabPanel p={2}>
								<Grid
									templateColumns={{
										base: "repeat(2, 1fr)",
										md: "repeat(3, 1fr)",
									}}
									gap={3}
								>
									{[
										{
											value: "none",
											icon: Seperator_None,
										},
										{
											value: "bottom_separator_1",
											icon: BottomSeparator_Curve1,
										},
										{
											value: "bottom_separator_2",
											icon: BottomSeparator_Curve2,
										},
										{
											value: "bottom_separator_3",
											icon: BottomSeparator_Curve3,
										},
										{
											value: "bottom_separator_4",
											icon: BottomSeparator_Rounded1,
										},
										{
											value: "bottom_separator_5",
											icon: BottomSeparator_Rounded2,
										},
										{
											value: "bottom_separator_6",
											icon: BottomSeparator_Rounded3,
										},
										{
											value: "bottom_separator_7",
											icon: BottomSeparator_Slant1,
										},
										{
											value: "bottom_separator_8",
											icon: BottomSeparator_Slant2,
										},
										{
											value: "bottom_separator_9",
											icon: BottomSeparator_Straight,
										},
										{
											value: "bottom_separator_10",
											icon: BottomSeparator_Wave1,
										},
										{
											value: "bottom_separator_11",
											icon: BottomSeparator_Wave2,
										},
										{
											value: "bottom_separator_12",
											icon: BottomSeparator_Wave3,
										},
										{
											value: "bottom_separator_13",
											icon: BottomSeparator_Wave4,
										},
									].map((item, idx) => {
										const isSelected =
											bottomSeparator?.bottomSeparatorIcon ===
											item.value;

										return (
											<Box
												key={item.value}
												bg="gray.50"
												role="button"
												tabIndex={0}
												onClick={() =>
													setAttributes({
														bottomSeparator: {
															bottomSeparator: 1,
															bottomSeparatorIcon:
																item.value,
														},
													})
												}
												borderRadius="base"
												border={
													isSelected
														? "2px solid"
														: "1px solid"
												}
												borderColor={
													isSelected
														? "purple.500"
														: "gray.200"
												}
												height="70px"
												display="flex"
												flexDirection="column"
												alignItems="center"
												justifyContent="center"
												cursor="pointer"
												p={1}
											>
												<Box
													as={item.icon}
													width="full"
													height="full"
												/>
												<Text
													fontSize="xs"
													fontWeight="semibold"
													mb={0}
													pt={0.5}
												>
													{__(
														"Style",
														"magazine-blocks"
													)}{" "}
													{idx + 1}
												</Text>
											</Box>
										);
									})}
								</Grid>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Panel>
				<Panel title={__("Border", "magazine-blocks")}>
					<Tabs variant="border">
						<TabList>
							<Tab>{__("Normal", "magazine-blocks")}</Tab>
							<Tab>{__("Hover", "magazine-blocks")}</Tab>
						</TabList>
						<TabPanels>
							<TabPanel>
								<Border
									value={border}
									onChange={(v) => {
										setAttributes({
											border: v,
										});
									}}
									resetAttributeKey="border"
								/>
								<BoxShadow
									resetAttributeKey="boxShadow"
									value={boxShadow}
									onChange={(val) =>
										setAttributes({
											boxShadow: val,
										})
									}
								/>
							</TabPanel>
							<TabPanel>
								<Border
									value={borderHover}
									onChange={(v) => {
										setAttributes({
											borderHover: v,
										});
									}}
									resetAttributeKey="borderHover"
								/>
								<BoxShadow
									resetAttributeKey="boxShadowHover"
									value={boxShadowHover}
									onChange={(val) =>
										setAttributes({
											boxShadowHover: val,
										})
									}
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Panel>
				<Panel title={__("Overlay", "magazine-blocks")}>
					<Toggle
						checked={overlay}
						onChange={() => setAttributes({ overlay: !overlay })}
						label={__("Enable", "magazine-blocks")}
					/>
					{overlay && (
						<PopoverDrawer label={__("Type", "magazine-blocks")}>
							<Box padding={4}>
								<Reset
									saved={overlayBackground}
									resetKey="overlayBackground"
									onReset={(v) => {
										setAttributes({ overlayBackground: v });
									}}
									buttonProps={{
										position: "absolute",
										top: "12px",
										right: "10px",
									}}
								/>
								<Background
									value={overlayBackground}
									onChange={(v) =>
										setAttributes({ overlayBackground: v })
									}
								/>
							</Box>
						</PopoverDrawer>
					)}
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
